import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  EVENT_LOCATIONS,
  HOME_LOCATIONS,
  API_URL,
  WHATSAPP_NUMBER,
} from "../data/services";
import { useLanguage } from "../i18n/LanguageContext";
import { loadCartItems, clearCart } from "../utils/cart";

const TIME_SLOTS = ["8:00 AM", "9:30 AM", "11:00 AM", "12:30 PM", "2:00 PM", "3:30 PM", "5:00 PM", "6:30 PM"];

const emptyForm = { name: "", phone: "", address: "", date: "", time: "", location: "", notes: "" };

export default function BookingFormPage() {
  const { t, lang } = useLanguage();
  const [items] = useState(loadCartItems);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const hasEvent = items.some((i) => i.category === "Surprise Event Management");
  const locOptions = hasEvent ? EVENT_LOCATIONS : HOME_LOCATIONS;
  const location = form.location && locOptions.includes(form.location) ? form.location : locOptions[0];

  const total = items.reduce((s, i) => s + (i.price || 0) * i.qty, 0);
  const count = items.reduce((s, i) => s + i.qty, 0);

  const submit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = true;
    if (!form.phone.trim()) errs.phone = true;
    if (!form.address.trim()) errs.address = true;
    if (!form.date) errs.date = true;
    if (!form.time) errs.time = true;
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error(t.page.required);
      return;
    }

    const lines = ["New Booking — Alpha & More", "Items:"];
    items.forEach((i) =>
      lines.push(i.price != null ? `- ${i.label} x${i.qty} = ₹${i.price * i.qty}` : `- ${i.label}`)
    );
    if (total > 0) lines.push(`Estimated Total: ₹${total}`);
    lines.push(
      "",
      `Name: ${form.name}`,
      `Phone: ${form.phone}`,
      `Preferred Date: ${form.date}`,
      `Time Slot: ${form.time}`,
      `Location: ${location}`,
      `Address: ${form.address}`
    );
    if (form.notes) lines.push(`Notes: ${form.notes}`);

    axios
      .post(`${API_URL}/bookings`, {
        service: items.length === 1 ? items[0].label : `${items.length} services`,
        category: [...new Set(items.map((i) => i.category))].join(", "),
        items: items.map((i) => ({ label: i.label, price: i.price, qty: i.qty })),
        total: total || null,
        name: form.name,
        phone: form.phone,
        date: form.date,
        time: form.time,
        location,
        address: form.address,
        notes: form.notes,
      })
      .catch(() => {});

    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
    clearCart();
    toast.success(t.booking.toast);
  };

  const fieldCls = (bad) =>
    `h-12 text-base rounded-2xl ${bad ? "border-red-500 ring-2 ring-red-500/40" : ""}`;

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4" data-testid="booking-form-page">
        <div className="rounded-3xl bg-white border border-stone-200 p-10 text-center max-w-md">
          <p className="text-lg text-stone-600">{t.page.empty}</p>
          <Link
            to="/"
            data-testid="bf-back-empty"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#E85D75] text-white font-semibold px-6 py-3"
          >
            <ArrowLeft className="w-4 h-4" /> {t.page.backHome}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-stone-900" data-testid="booking-form-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <div className="flex items-center justify-between mb-8">
          <Link
            to="/book"
            data-testid="bf-back-selection"
            className="inline-flex items-center gap-2 text-base font-medium text-stone-600 hover:text-[#E85D75] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" /> {t.page.backToSelection}
          </Link>
          <img src="/assets/logo.png" alt="Alpha & More" className="h-11 w-11 rounded-full object-cover" />
        </div>

        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
          {t.page.yourDetails}
        </h1>

        <div className="mt-6 rounded-3xl bg-white border border-stone-200 p-5" data-testid="form-summary">
          <p className="text-xs font-semibold tracking-widest text-stone-400 uppercase mb-3">
            {t.page.summary}
          </p>
          <div className="space-y-2">
            {items.map((i, idx) => (
              <div
                key={i.key}
                data-testid={`form-summary-item-${idx}`}
                className="flex items-center justify-between gap-3 text-base"
              >
                <span className="text-stone-800 font-medium min-w-0 leading-snug">
                  {(lang === "bn" && i.labelBn) || i.label}
                  {i.qty > 1 ? ` × ${i.qty}` : ""}
                </span>
                <span className="text-stone-600 shrink-0">
                  {i.price != null ? `₹${(i.price * i.qty).toLocaleString("en-IN")}` : t.priceOnEnquiry}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center justify-between rounded-2xl bg-stone-900 text-white px-4 py-3">
            <span className="text-sm font-medium">
              {count} {t.itemsWord}
            </span>
            <span className="text-lg font-bold" data-testid="form-page-total">
              ₹{total.toLocaleString("en-IN")}
            </span>
          </div>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-5" noValidate>
          <div className="space-y-2">
            <Label htmlFor="bp-name" className="text-base font-semibold">{t.booking.name}</Label>
            <Input
              id="bp-name"
              data-testid="bp-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={t.booking.namePh}
              className={fieldCls(errors.name)}
            />
            {errors.name && <p className="text-sm font-medium text-red-600" data-testid="bp-error-name">{t.page.required}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="bp-phone" className="text-base font-semibold">{t.booking.phone}</Label>
            <Input
              id="bp-phone"
              data-testid="bp-phone-input"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder={t.booking.phonePh}
              className={fieldCls(errors.phone)}
            />
            {errors.phone && <p className="text-sm font-medium text-red-600" data-testid="bp-error-phone">{t.page.required}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="bp-address" className="text-base font-semibold">{t.booking.address}</Label>
            <Textarea
              id="bp-address"
              data-testid="bp-address-input"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder={t.booking.addressPh}
              rows={2}
              className={`text-base rounded-2xl ${errors.address ? "border-red-500 ring-2 ring-red-500/40" : ""}`}
            />
            {errors.address && <p className="text-sm font-medium text-red-600" data-testid="bp-error-address">{t.page.required}</p>}
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="bp-date" className="text-base font-semibold">{t.booking.date}</Label>
              <Input
                id="bp-date"
                data-testid="bp-date-input"
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value, time: "" })}
                className={fieldCls(errors.date)}
              />
              {errors.date && <p className="text-sm font-medium text-red-600" data-testid="bp-error-date">{t.page.required}</p>}
            </div>
            <div className="space-y-2">
              <Label className="text-base font-semibold">{t.booking.location}</Label>
              <Select value={location} onValueChange={(v) => setForm({ ...form, location: v })}>
                <SelectTrigger data-testid="bp-location-select" className="h-12 text-base rounded-2xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {locOptions.map((l) => (
                    <SelectItem key={l} value={l} data-testid={`bp-location-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                      {t.locations[l] || l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.date && (
            <div className="space-y-2">
              <Label className="text-base font-semibold">{t.booking.timeSlot}</Label>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="bp-timeslots">
                {TIME_SLOTS.map((slot) => (
                  <button
                    type="button"
                    key={slot}
                    onClick={() => setForm({ ...form, time: slot })}
                    data-testid={`bp-slot-${slot.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    className={`rounded-xl border px-2 py-3 text-sm font-bold transition-colors duration-150 ${
                      form.time === slot
                        ? "bg-white text-[#457B9D] border-[#457B9D] ring-2 ring-[#457B9D]/40"
                        : "bg-[#457B9D] text-white border-[#457B9D] hover:bg-[#386682]"
                    }`}
                  >
                    {slot}
                  </button>
                ))}
              </div>
              {errors.time && <p className="text-sm font-medium text-red-600" data-testid="bp-error-time">{t.page.requiredSlot}</p>}
            </div>
          )}
          {!form.date && errors.time && (
            <p className="text-sm font-medium text-red-600" data-testid="bp-error-time-nodate">{t.page.required}</p>
          )}

          <div className="space-y-2">
            <Label htmlFor="bp-notes" className="text-base font-semibold">{t.booking.notes}</Label>
            <Textarea
              id="bp-notes"
              data-testid="bp-notes-input"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder={t.booking.notesPh}
              rows={2}
              className="text-base rounded-2xl"
            />
          </div>

          <button
            type="submit"
            data-testid="bp-submit-button"
            className="w-full rounded-full bg-[#25D366] text-white text-lg font-bold py-4 hover:bg-[#1eb85c] transition-colors"
          >
            {t.page.sendWhatsApp}
          </button>
        </form>
      </div>
    </div>
  );
}
